import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Autocomplete,
  TextField,
  Typography,
  Link,
} from '@mui/material';
import api from '../../utils/api';

/**
 * Share bench via email to a Contact (must have email). Optionally create contact first.
 */
export default function BenchShareDialog({ open, onClose, benchId, listPath }) {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);

  const loadContacts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/contacts', { params: { limit: 500, status: 'active' } });
      const data = res.data?.data;
      const rows = Array.isArray(data) ? data : [];
      setContacts(rows.filter((c) => c.email && String(c.email).trim()));
    } catch {
      setContacts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      setError('');
      setSelected(null);
      loadContacts();
    }
  }, [open, loadContacts]);

  const handleSend = async () => {
    if (!selected?.id) {
      setError('Select a contact with an email.');
      return;
    }
    setSending(true);
    setError('');
    try {
      await api.post(`/bench-sales/${benchId}/share`, { contact_id: selected.id });
      onClose(true);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  const newContactHref = `/contacts/new${listPath ? `?returnTo=${encodeURIComponent(listPath)}` : ''}`;

  return (
    <Dialog open={open} onClose={() => onClose(false)} maxWidth="sm" fullWidth>
      <DialogTitle>Share bench by email</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Pick a contact — the invite is sent to their email on file.
        </Typography>
        <Autocomplete
          options={contacts}
          loading={loading}
          getOptionLabel={(o) => `${o.first_name || ''} ${o.last_name || ''} (${o.email || ''})`.trim()}
          value={selected}
          onChange={(e, v) => setSelected(v)}
          renderInput={(params) => <TextField {...params} label="Contact" placeholder="Search" />}
        />
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2">
            Need someone new?{' '}
            <Link href={newContactHref} target="_blank" rel="noopener noreferrer">
              Create contact
            </Link>{' '}
            — then reopen this dialog to refresh the list.
          </Typography>
        </Box>
        {error ? (
          <Typography color="error" variant="body2" sx={{ mt: 1 }}>
            {error}
          </Typography>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose(false)}>Cancel</Button>
        <Button variant="contained" onClick={handleSend} disabled={sending || !selected}>
          Send email
        </Button>
      </DialogActions>
    </Dialog>
  );
}
